import React, {useEffect, useState} from 'react';
import {useTask} from "../../../models/task/use-task";
import {useTranslate} from "../../../system/useTranslate";
import AccordionDataView from "../../tasks/components/accordion-dataview";
import {Checkbox} from "primereact/checkbox";
import Avatar from "../../../system/components/avatar";
import {isPast} from "../../../system/utils";
import TaskSidebar from "../components/task-sidebar";
import {TaskDateTime} from "../../tasks/components/task-time-range";
import Icon from "../../../system/components/icon";
import {useNavigate} from "react-router-dom";
import {usePermission} from "../../../system/usePermission";
import TaskListFilter from "../../tasks/components/task-list-filter";
import TaskFocusControls from "../../../system/focus/task-focus-controls";
import {RecurrenceBadge} from "../../tasks/components/task-recurrence-badge";
import {useSelector} from "react-redux";
import TaskDecisionTag from "../meetings/task-decision-tag";

function Tasks({tasks, createTask, updateTask, projectData}) {

    const [tabs, setTabs] = useState([]);
    const task = useTask();
    const navigate = useNavigate();
    const translate = useTranslate();
    const [selectedTask, setSelectedTask] = useState();
    const [filteredTasks, setFilteredTasks] = useState([]);
    const permission = usePermission();
    const permissionSignature = permission.permissions.join("|");
    const {workspaceMembershipData} = useSelector(state => state.app);
    const canUpdateTaskStatus = (taskData) => Boolean(
        taskData?.assignee && workspaceMembershipData &&
        String(taskData.assignee.documentId || taskData.assignee.id)
        === String(workspaceMembershipData.documentId || workspaceMembershipData.id),
    );

    useEffect(() => {
        setFilteredTasks(tasks ?? []);

    }, [tasks]);

    useEffect(() => {
        if (projectData){
            permission.setScopeId(projectData.documentId);
            permission.setScopeRolePermissions(projectData?.rolesConfig);
        }
    }, [projectData]);

    useEffect(() => {

        if (filteredTasks) {
            setTabs([
                {title: translate("overdue"),
                    value: task.filter(filteredTasks, "overdue"),
                    className: "text-red-500"},
                {
                    title: translate("today"),
                    value: task.filter(filteredTasks, "today"),
                    placeholder: translate("add-tasks-for-time-remain").replace("{{TIME_REMAIN}}", translate("today")),
                    quickCreateCallback: permission.can("create-task")? (e) => {
                        e.value && createTask({title: e.value})
                    }:undefined
                },
                {
                    title: translate("this-week"),
                    value: task.filter(filteredTasks, "this-week"),
                    placeholder: translate("add-tasks-for-time-remain").replace("{{TIME_REMAIN}}", translate("this-week")),
                    quickCreateCallback: permission.can("create-task")? (e) => {
                        e.value && createTask({title: e.value})
                    }:undefined
                },
                {title: translate("future"), value: task.filter(filteredTasks, "future")},
                {title: translate("no-due"), value: task.filter(filteredTasks, "no-due")},
                {
                    title: translate("done"),
                    value: task.filter(filteredTasks, "done"),
                    placeholder: translate("add-tasks-for-time-remain").replace("{{TIME_REMAIN}}", translate("this-week"))
                }
            ]);
        }
    }, [filteredTasks, tasks, permissionSignature]);


    return (
        <div className="h-full overflow-hidden">
            <TaskListFilter showDateFilter tasks={tasks} onChange={setFilteredTasks}/>

            <AccordionDataView tabs={tabs} activeIndex={[0, 1, 2, 4]} columns={[
                {
                    header: "",
                    headerClassName: "w-4rem",
                    cellClassName: "w-4rem",
                    render: (row) => <div className="pr-2">
                        <Checkbox disabled={!permission.can("change-task-status") || !canUpdateTaskStatus(row)} checked={row.taskStatus === "done"}
                                                                     onChange={(e) => {
                                                                         if (!canUpdateTaskStatus(row)) {
                                                                             alert('فقط مسئول این کار اجازه تغییر وضعیت را دارد.');
                                                                             return;
                                                                         }

                                                                         updateTask(row, {
                                                                             doneAt: row?.taskStatus === "done" ? null : Date.now(),
                                                                             taskStatus: row?.taskStatus === "done" ? "todo" : "done"
                                                                         })
                                                                     }}
                        />
                    </div>
                },
                {
                    header: translate("task-title"),
                    headerClassName: "flex-1",
                    cellClassName: "flex-1",
                    render: (row) => (
                        <div data-task-sidebar-trigger="true" className="flex align-items-center gap-1 w-full h-full">
                            <span className="inline-flex align-items-center gap-2" dir="ltr">
                                <span dir="rtl" className={row.occurrenceStatus === "skipped" ? "line-through text-500" : ""}>{row.title}</span>
                                <TaskFocusControls task={row} compact/>
                                <RecurrenceBadge task={row}/>
                            </span>
                            <TaskDecisionTag task={row}/>
                            {
                                row.parentTask && (
                                    <>
                                        <Icon icon="mi mi:chevron_right" className="text-sm text-400"/>
                                        <span className="text-3xs text-400 font-bold">{row.parentTask?.title}</span>
                                    </>
                                )
                            }
                            {row.childrenTasks?.length > 0 && (
                                <span
                                    className="text-xs surface-200 border-1 border-300 border-round px-2 flex align-items-center gap-1">
                                <Icon icon="mi mi:graph_1" className="text-sm"/>
                                    {row.childrenTasks?.length}
                            </span>
                            )}

                        </div>
                    ),
                    onCellClick: (e) => {
                        setSelectedTask(e)
                    }
                },
                {
                    header: translate("assignee"),
                    headerClassName: "w-7rem",
                    cellClassName: "w-7rem",
                    render: (row) => <Avatar memberData={row.assignee?.member}/>
                },
                {
                    header: translate("startAt"),
                    headerClassName: "w-10rem",
                    cellClassName: "w-10rem text-xs",
                    render: (row) => <TaskDateTime value={row.startAt}/>
                },
                {
                    header: translate("dueDate"),
                    headerClassName: "w-10rem",
                    cellClassNameDynamic: (row) => `w-10rem text-xs ${row.dueDate && isPast(new Date(row.dueDate).getTime() / 1000, 1, "days") ? "text-red-500" : ""}`,
                    render: (row) => <TaskDateTime value={row.dueDate}/>
                },
                {
                    header: translate("projects"),
                    headerClassName: "w-7rem",
                    cellClassName: "w-7rem",
                    render: (row) => <div className="w-7rem flex flex-wrap gap-1">{row.projects?.map(p => (
                        <span onClick={() => {
                            navigate(`/projects/${p.documentId}`)
                        }}
                              className="border-round px-1 border-1 border-200 cursor-pointer surface-100 text-2xs">{p.title}</span>))}</div>
                },
            ]}/>


            {/*<Table onRowClick={(e)=>{setSelectedTask(e.value)}} style={null} footer={<QuickCreateInputText onKeyDownEnter={(e)=>{e.value&&createTask({title:e.value})}} icon={"mi mi:add"} placeholder={translate("add-tasks")}/>} onRowSelect={(e)=>{updateTask(e.data, {taskStatus:e.checked?"done":"todo"})}} rowClassName={(data)=>{return data.taskStatus==="done"?"line-through surface-100 text-300":""}} selection={tasks.filter(task=>task.taskStatus==="done")} dataKey="id" rowGroupMode="subheader" expandedRows={expandedRows} rowGroupHeaderTemplate={rowGroupHeaderTemplate} rowGroupFooterTemplate={rowGroupFooterTemplate} groupRowsBy="timeRemain" showFooter={false} fields={task.prepareTable({config, actions:{}}).fields} fieldsConfig={task.prepareTable({config, actions:{}}).fieldsConfig} showHeader={false} value={tasks} selectionMode="multiple"/>*/}


            {/*<AccordionDataView tabs={tabs} activeIndex={[0,1,2]} onRowClick={(e)=>{setSelectedTask(e)}} />*/}

            {tasks && <TaskSidebar disabled={!permission.can("edit-task")} onOtherTaskClicked={(e) => {
                setSelectedTask(tasks.find(task => task.id === e?.id))
            }} selectedTask={tasks.find(task => task.id === selectedTask?.id)} setSelectedTask={setSelectedTask}
                                   updateTask={updateTask}/>}


            {/*<DataTable showSelectAll={false} rowClassName={(data)=>{return selection.find(s=>s.name===data.name)?" line-through":""}} selectionMode="multiple" selection={selection} onSelectionChange={(e)=>{setSelection(e.value)}} expandedRowIcon="pi pi-times" scrollHeight="flex" className="h-full ltr" groupRowsBy="timeRemain" rowGroupHeaderTemplate={(data)=>(<div>{data.timeRemain}</div>)} rowGroupMode="subheader" scrollable value={tasks}>*/}
            {/*    <Column selectionMode="multiple"/>*/}
            {/*    <Column header="title" field="title"  />*/}
            {/*    <Column header="timeRemain" field="timeRemain"/>*/}
            {/*</DataTable>*/}
        </div>)
}


export default Tasks;
